import styled from '@emotion/styled';

import theme from 'theme';
import {getQAAttribute} from 'common';

import {CalendarMode} from 'Calendar/types';

import {Tooltip} from 'Tooltip';

import {getNewHoverDate, getNewRange} from '../utils';

import type {CalendarContextProps, CalendarProps} from 'Calendar/types';

import type {DayProps} from './types';

export * from './utils';
export * from './types';

const Container = styled.div<Omit<DayProps, 'date' | 'tooltipContent'>>`
  position: relative;
  display: flex;
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  user-select: none;
  cursor: ${props => (props.isDisabled ? 'default' : 'pointer')};

  ::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 36px;
    height: 36px;
    box-sizing: border-box;
    border-style: solid;
    border-radius: 4px;
    border-width: 1px;
    border-color: ${props => (props.isToday ? theme.colors.accent[90] : 'transparent')};
  }

  ${({isHidden, isViewMonth, isSelected, isRangeStart, isRangeEnd, isRangeIn, isRangeHover, isDisabled}) => {
    if (isHidden) {
      return {
        visibility: 'hidden',
        pointerEvents: 'none',
      };
    }

    if (isDisabled) {
      return {
        color: theme.colors.grey[40],
      };
    }

    if (isSelected || (isRangeStart && isRangeEnd)) {
      return {
        borderRadius: '4px',
        color: theme.colors.white,
        backgroundColor: theme.colors.accent[90],
      };
    }

    if (isRangeStart) {
      return {
        borderRadius: '4px 0 0 4px',
        color: theme.colors.white,
        backgroundColor: theme.colors.accent[90],
      };
    }

    if (isRangeEnd) {
      return {
        borderRadius: '0 4px 4px 0',
        color: theme.colors.white,
        backgroundColor: theme.colors.accent[90],
      };
    }

    if (isRangeIn || isRangeHover) {
      return {
        color: theme.colors.grey[isViewMonth ? 100 : 60],
        backgroundColor: theme.colors.accent[10],

        '&:hover': {
          borderRadius: '4px',
          backgroundColor: theme.colors.accent[20],
        },
      };
    }

    if (!isViewMonth) {
      return {
        color: theme.colors.grey[60],

        '&:hover': {
          borderRadius: '4px',
          backgroundColor: theme.colors.accent[20],
        },
      };
    }

    return {
      backgroundColor: theme.colors.white,

      '&:hover': {
        borderRadius: '4px',
        backgroundColor: theme.colors.accent[20],
      },
    };
  }}
`;

export const getDayComponent = (context: CalendarContextProps, components: CalendarProps['components'], qa: string) => {
  if (components?.day) {
    const Component = components.day;

    return (props: DayProps) => <Component key={props.date.getTime()} {...props} />;
  }

  const {
    mode,
    onChange,
    hoverDate,
    setHoverDate,
    value: [rangeStart, rangetEnd],
  } = context;

  const hasHover = mode === CalendarMode.Range && (!rangeStart || !rangetEnd);

  const getQA = getQAAttribute(qa);

  return ({date, tooltipContent, ...props}: DayProps) => {
    const dayValue = date.getDate();
    const timeValue = date.getTime();

    const events = {
      onClick: () => {
        onChange(getNewRange(date, context));
      },

      onMouseEnter: !hasHover
        ? undefined
        : () => {
            setHoverDate(getNewHoverDate(date, context));
          },

      onMouseLeave: !hasHover
        ? undefined
        : () => {
            if (timeValue === hoverDate?.getTime()) {
              setHoverDate(undefined);
            }
          },
    };

    const child = (
      <Container key={timeValue} data-qa={getQA(dayValue, props)} {...props} {...events}>
        {dayValue}
      </Container>
    );

    if (tooltipContent) {
      return (
        <Tooltip key={timeValue} content={tooltipContent}>
          {child}
        </Tooltip>
      );
    }

    return child;
  };
};
